import { createFileRoute } from '@tanstack/react-router'
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Users,
  UserCheck,
  Mail,
  MapPin,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listUsers, createUser, updateUser, deleteUser } from "@/lib/api/users";
import { listGroups } from "@/lib/api/groups";
import { getErrorMessage } from "@/lib/api/client";
import { ManagerGuard } from "@/lib/auth/AdminGuard";
import { useIsAdmin } from "@/lib/auth/store";
import type { User, UserRole, UserScope, Group } from "@/types";

export const Route = createFileRoute('/_dashboard/users')({
  component: UsersPage,
})

const roleLabels: Record<UserRole, { label: string; color: string }> = {
  admin: { label: "Administrateur", color: "bg-error/20 text-error" },
  manager: { label: "Responsable", color: "bg-laposte-navy/20 text-laposte-navy" },
  user: { label: "Utilisateur", color: "bg-info/20 text-info" },
  viewer: { label: "Consultant", color: "bg-muted text-muted-foreground" },
};

const scopeLabels: Record<UserScope, string> = {
  national: "National",
  regional: "Régional",
};

const createUserSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(8, "Minimum 8 caractères"),
  full_name: z.string().optional(),
  role: z.enum(["viewer", "user", "manager", "admin"]),
  scope: z.enum(["national", "regional"]),
  is_active: z.boolean(),
  group_ids: z.array(z.number()).default([]),
});

const updateUserSchema = z.object({
  email: z.string().email("Email invalide").optional(),
  password: z.string().min(8, "Minimum 8 caractères").optional().or(z.literal("")),
  full_name: z.string().optional(),
  role: z.enum(["viewer", "user", "manager", "admin"]).optional(),
  scope: z.enum(["national", "regional"]).optional(),
  is_active: z.boolean().optional(),
  group_ids: z.array(z.number()).optional(),
});

type CreateUserForm = z.infer<typeof createUserSchema>;
type UpdateUserForm = z.infer<typeof updateUserSchema>;

function GroupBadges({ groups }: { groups: Group[] }) {
  if (!groups || groups.length === 0) return <span className="text-muted-foreground text-sm">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {groups.map((g) => (
        <Badge key={g.id} variant="outline" className="text-xs">
          {g.name}
        </Badge>
      ))}
    </div>
  );
}

function GroupMultiSelect({
  groups,
  selectedIds,
  onChange,
}: {
  groups: Group[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px]">
      {groups.map((g) => {
        const active = selectedIds.includes(g.id);
        return (
          <Badge
            key={g.id}
            variant={active ? "default" : "outline"}
            className="cursor-pointer select-none"
            onClick={() =>
              onChange(
                active
                  ? selectedIds.filter((id) => id !== g.id)
                  : [...selectedIds, g.id]
              )
            }
          >
            {g.name}
          </Badge>
        );
      })}
      {groups.length === 0 && (
        <span className="text-muted-foreground text-sm">Aucun groupe disponible</span>
      )}
    </div>
  );
}

function UsersPage() {
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<UserRole | "all">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [page, setPage] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const pageSize = 10;

  const { data, isLoading } = useQuery({
    queryKey: ["users", { page, pageSize, search, role: roleFilter === "all" ? undefined : roleFilter, is_active: statusFilter === "all" ? undefined : statusFilter === "active" }],
    queryFn: () =>
      listUsers({
        page,
        pageSize,
        search: search || undefined,
        role: roleFilter === "all" ? undefined : roleFilter,
        is_active: statusFilter === "all" ? undefined : statusFilter === "active",
      }),
  });

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => listGroups(),
  });
  const allGroups = groupsData?.items ?? [];

  // Roles a manager can assign (not admin/manager)
  const assignableRoles = isAdmin
    ? Object.entries(roleLabels)
    : Object.entries(roleLabels).filter(([r]) => r !== "admin" && r !== "manager");

  const createForm = useForm<CreateUserForm>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: "",
      password: "",
      full_name: "",
      role: "user",
      scope: "regional",
      is_active: true,
      group_ids: [],
    },
  });

  const editForm = useForm<UpdateUserForm>({
    resolver: zodResolver(updateUserSchema),
  });

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Utilisateur créé");
      setCreateDialogOpen(false);
      createForm.reset();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserForm }) =>
      updateUser(id, {
        ...data,
        password: data.password || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Utilisateur mis à jour");
      setEditDialogOpen(false);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      updateUser(id, { is_active }),
    onSuccess: (_, { is_active }) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(is_active ? "Utilisateur activé" : "Utilisateur désactivé");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Utilisateur supprimé");
      setDeleteDialogOpen(false);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    editForm.reset({
      email: user.email,
      full_name: user.full_name || "",
      role: user.role,
      scope: user.scope,
      is_active: user.is_active,
      password: "",
      group_ids: user.groups?.map((g) => g.id) ?? [],
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const getInitials = (user: User) => {
    if (user.full_name) {
      return user.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return user.email.slice(0, 2).toUpperCase();
  };

  return (
    <ManagerGuard>
    <div className="flex flex-col h-full">
      <Header title="Utilisateurs" />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Actions bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={roleFilter}
              onValueChange={(value) => {
                setRoleFilter(value as UserRole | "all");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                {Object.entries(roleLabels).map(([role, { label }]) => (
                  <SelectItem key={role} value={role}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as "all" | "active" | "inactive");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouvel utilisateur
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Périmètre</TableHead>
                  <TableHead>Groupes</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><div className="flex items-center gap-3"><Skeleton className="h-10 w-10 rounded-full" /><Skeleton className="h-4 w-32" /></div></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                    </TableRow>
                  ))
                ) : data?.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Users className="h-10 w-10 opacity-50" />
                        <p>Aucun utilisateur trouvé</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.items.map((user, index) => (
                    <TableRow
                      key={user.id}
                      className="border-b transition-colors hover:bg-muted/50"
                      style={{ animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both` }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={user.profile_picture || undefined} />
                            <AvatarFallback>{getInitials(user)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{user.full_name || user.pseudo || "Sans nom"}</p>
                            {user.pseudo && <p className="text-sm text-muted-foreground">@{user.pseudo}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          {user.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleLabels[user.role]?.color}>
                          {roleLabels[user.role]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          {user.scope === "national" ? <Globe className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
                          {scopeLabels[user.scope]}
                        </div>
                      </TableCell>
                      <TableCell>
                        <GroupBadges groups={user.groups} />
                      </TableCell>
                      <TableCell>
                        {user.is_active ? (
                          <Badge variant="success" className="gap-1">
                            <UserCheck className="h-3 w-3" />
                            Actif
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Inactif</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => toggleActiveMutation.mutate({ id: user.id, is_active: !user.is_active })}
                            >
                              <UserCheck className="mr-2 h-4 w-4" />
                              {user.is_active ? "Désactiver" : "Activer"}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(user)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(user)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {data.total_items} utilisateur{data.total_items > 1 ? "s" : ""} au total
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Précédent
              </Button>
              <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
                {page} / {data.total_pages}
              </div>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))} disabled={page === data.total_pages}>
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvel utilisateur</DialogTitle>
            <DialogDescription>Créez un nouveau compte utilisateur</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={createForm.handleSubmit((data) => createMutation.mutate(data))}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="create-email">Email</Label>
              <Input id="create-email" type="email" {...createForm.register("email")} />
              {createForm.formState.errors.email && (
                <p className="text-sm text-destructive">{createForm.formState.errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Mot de passe</Label>
              <Input id="create-password" type="password" {...createForm.register("password")} />
              {createForm.formState.errors.password && (
                <p className="text-sm text-destructive">{createForm.formState.errors.password.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-name">Nom complet</Label>
              <Input id="create-name" {...createForm.register("full_name")} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select
                  value={createForm.watch("role")}
                  onValueChange={(value) => createForm.setValue("role", value as UserRole)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map(([role, { label }]) => (
                      <SelectItem key={role} value={role}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Périmètre</Label>
                <Select
                  value={createForm.watch("scope")}
                  onValueChange={(value) => createForm.setValue("scope", value as UserScope)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regional">Régional</SelectItem>
                    <SelectItem value="national">National</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Groupes (régions)</Label>
              <GroupMultiSelect
                groups={allGroups}
                selectedIds={createForm.watch("group_ids") ?? []}
                onChange={(ids) => createForm.setValue("group_ids", ids)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="create-active"
                checked={createForm.watch("is_active")}
                onCheckedChange={(checked) => createForm.setValue("is_active", checked)}
              />
              <Label htmlFor="create-active">Compte actif</Label>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Création..." : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier l&apos;utilisateur</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={editForm.handleSubmit((data) =>
              selectedUser && updateMutation.mutate({ id: selectedUser.id, data })
            )}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input id="edit-email" type="email" {...editForm.register("email")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">Nouveau mot de passe (laisser vide pour ne pas modifier)</Label>
              <Input id="edit-password" type="password" {...editForm.register("password")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nom complet</Label>
              <Input id="edit-name" {...editForm.register("full_name")} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select
                  value={editForm.watch("role")}
                  onValueChange={(value) => editForm.setValue("role", value as UserRole)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map(([role, { label }]) => (
                      <SelectItem key={role} value={role}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Périmètre</Label>
                <Select
                  value={editForm.watch("scope")}
                  onValueChange={(value) => editForm.setValue("scope", value as UserScope)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regional">Régional</SelectItem>
                    <SelectItem value="national">National</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Groupes (régions)</Label>
              <GroupMultiSelect
                groups={allGroups}
                selectedIds={editForm.watch("group_ids") ?? []}
                onChange={(ids) => editForm.setValue("group_ids", ids)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-active"
                checked={editForm.watch("is_active")}
                onCheckedChange={(checked) => editForm.setValue("is_active", checked)}
              />
              <Label htmlFor="edit-active">Compte actif</Label>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l&apos;utilisateur</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer {selectedUser?.full_name || selectedUser?.email} ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedUser && deleteMutation.mutate(selectedUser.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </ManagerGuard>
  );
}
